import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import rbacService from "../services/rbacService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch user permissions from backend (session cookie)
  const fetchMyPermissions = useCallback(async () => {
    try {
      const permissionList = await rbacService.getMyPermissions();
      setPermissions(permissionList);
      setIsAuthenticated(true);
      return permissionList;
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setPermissions([]);
      setIsAuthenticated(false);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyPermissions();
  }, [fetchMyPermissions]);

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  // Check if user has any of the given permissions
  const hasAnyPermission = (permissionArray) => {
    return permissionArray.some((permission) => permissions.includes(permission));
  };

  // Check if user has all of the given permissions
  const hasAllPermissions = (permissionArray) => {
    return permissionArray.every((permission) => permissions.includes(permission));
  };

  // Set user info and fetch permissions after login
  const login = async (userData) => {
    setUserInfo(userData);
    return fetchMyPermissions();
  };

  // Clear permissions and user info on logout
  const logout = () => {
    setPermissions([]);
    setUserInfo(null);
    setIsAuthenticated(false);
    setLoading(false);
  };

  const value = {
    permissions,
    userInfo,
    loading,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    login,
    logout,
    fetchMyPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
